/**
 * KAN-871: блок "Мой Telegram" во вкладке "Профиль" приложения менеджера:
 * личный Telegram-аккаунт, с которого менеджер пишет клиентам от себя.
 * Не подключён -> "Подключить" (шаги в MobilePersonalTelegramConnect);
 * подключён -> аккаунт, статус и "Отключить" с подтверждением.
 * Не путать с блоком "Уведомления" (бот уведомлений менеджера, KAN-835).
 * Логика в usePersonalTelegram; блока нет у тех, кто не менеджер.
 */
import { Loader2, Send } from 'lucide-react';
import { MobilePersonalTelegramConnect } from '@/components/mobile/MobilePersonalTelegramConnect';
import { usePersonalTelegram, type PersonalTelegramControl } from '@/hooks/usePersonalTelegram';
import { PERSONAL_TELEGRAM_TEXTS as T, personalTelegramStatusText } from '@/lib/personalTelegramTexts';
import type { PersonalTelegramAccount } from '@/services/api/personalTelegram';

const BUTTON = 'flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-medium disabled:opacity-60';

function ConnectedAccount({
  account,
  tg,
}: {
  account: PersonalTelegramAccount;
  tg: PersonalTelegramControl;
}): JSX.Element {
  const username = account.username.replace(/^@/, '');
  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-900" data-testid="personal-telegram-account">
        {username && <div className="font-medium">@{username}</div>}
        {account.phone && <div className="text-gray-600">{account.phone}</div>}
        {!username && !account.phone && <div>{account.label || account.account_id}</div>}
      </div>
      <p className="text-sm text-gray-700">
        Статус: <span data-testid="personal-telegram-status">{personalTelegramStatusText(account.status)}</span>
      </p>
      {tg.confirmingDisconnect ? (
        <div className="space-y-2 rounded-lg bg-gray-50 p-3">
          <p className="text-sm font-medium text-gray-900">{T.disconnectQuestion}</p>
          <p className="text-xs text-gray-600">{T.disconnectNote}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={tg.disconnect}
              disabled={tg.busy}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 text-sm font-medium text-white disabled:opacity-60"
              data-testid="personal-telegram-disconnect-confirm"
            >
              {tg.busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {T.disconnect}
            </button>
            <button
              type="button"
              onClick={tg.cancelDisconnect}
              disabled={tg.busy}
              className="h-11 flex-1 rounded-xl border border-gray-300 text-sm text-gray-700 disabled:opacity-60"
            >
              {T.cancel}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={tg.askDisconnect}
          className="h-11 w-full rounded-xl border border-gray-300 text-sm text-gray-700"
        >
          {T.disconnect}
        </button>
      )}
    </div>
  );
}

export function MobilePersonalTelegramBlock(): JSX.Element | null {
  const tg = usePersonalTelegram();
  if (!tg.available) return null;

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-testid="personal-telegram-block">
      <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
        <Send className="h-5 w-5 text-blue-600" aria-hidden="true" />
        {T.title}
      </h2>
      {tg.isLoading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label="Загрузка" />}
      {tg.isError && <p className="text-sm text-red-600">{T.loadFailed}</p>}
      {!tg.isLoading && !tg.isError && tg.account && <ConnectedAccount account={tg.account} tg={tg} />}
      {!tg.isLoading && !tg.isError && !tg.account && !tg.step && (
        <>
          <p className="text-xs text-gray-500">{T.intro}</p>
          <button
            type="button"
            onClick={tg.openConnect}
            className={`${BUTTON} bg-blue-600 text-white active:bg-blue-700`}
            data-testid="personal-telegram-connect"
          >
            {T.connect}
          </button>
        </>
      )}
      {!tg.account && tg.step && <MobilePersonalTelegramConnect tg={tg} />}
      {tg.error && (
        <p role="alert" className="text-sm text-red-600" data-testid="personal-telegram-error">
          {tg.error}
        </p>
      )}
    </section>
  );
}
